// src/pages/AutomationV2/mappers/conditions.mapper.ts
import { v4 as uuidv4 } from 'uuid';

import { flattenHierarchy } from '../utils/conditionHierarchy';

import type { AutomationRuleDTO, ConditionDTO, LogicalOp, Operator } from '../types/dto';
import type { ConditionGroup, ConditionLeaf, ConditionNode } from '../types/rule';

/**
 * Transforma a lista flat de condições vinda do backend (DTO) em uma árvore de draft (UI).
 * - Escolhe como raiz o primeiro nó com parent_id=null e is_group=true (ou o primeiro de root se não houver).
 * - Respeita a propriedade "order" para ordenar os filhos.
 */
export function dtoConditionsToTree(list: ConditionDTO[]): ConditionGroup | undefined {
  console.log('🌳 === INÍCIO buildTreeFromFlat ===');
  console.log('📥 Lista de condições recebida:', list);

  if (!list || list.length === 0) {
    console.log('⚠️ Lista vazia, retornando undefined');
    return undefined;
  }

  console.log('📊 Total de condições:', list.length);
  console.log('📋 Grupos:', list.filter(c => c.is_group).length);
  console.log('📋 Folhas:', list.filter(c => !c.is_group).length);

  const byParent: Record<string, ConditionDTO[]> = {};
  for (const c of list) {
    const key = String(c.parent_id ?? 'root');
    (byParent[key] ??= []).push(c);
  }

  console.log(
    '📊 Agrupamento por parent_id:',
    Object.keys(byParent).reduce(
      (acc, key) => {
        acc[key] = byParent[key].length;
        return acc;
      },
      {} as Record<string, number>
    )
  );

  const attach = (parentServerId: number | null, parentLogicalOp?: LogicalOp): ConditionNode[] => {
    const arr = byParent[String(parentServerId ?? 'root')] ?? [];
    arr.sort((a, b) => coerceOrder(a.order) - coerceOrder(b.order));
    return arr.map((c, index): ConditionNode => {
      if (c.is_group) {
        return {
          id: uuidv4(),
          type: 'group',
          logical: (c.logical_operator ?? 'AND') as LogicalOp,
          children: attach(c.id, c.logical_operator as LogicalOp | undefined),
        } as ConditionGroup;
      }
      // ✅ Popular meta com dados de extras (OTIMIZAÇÃO: resource_data)
      const meta: ConditionLeaf['meta'] = {};

      if (c.extras) {
        console.log('🔍 Populando meta com extras:', c.extras);

        // Dados básicos
        if (c.extras.form_uuid) {
          meta.form_uuid = c.extras.form_uuid;
        }
        if (c.extras.field_uuid) {
          meta.field_uuid = c.extras.field_uuid;
        }
        if (c.extras.field_label) {
          meta.field_label = c.extras.field_label;
        }

        // ✅ OTIMIZAÇÃO: Usar resource_data se disponível
        if (c.extras.resource_data) {
          console.log(
            '⚡ resource_data disponível - hidratação instantânea!',
            c.extras.resource_data
          );

          // Dados do campo
          if (c.extras.resource_data.field) {
            meta.field_uuid = c.extras.resource_data.field.id;
            meta.field_label = c.extras.resource_data.field.label;
            meta.form_uuid = c.extras.resource_data.field.form?.id;
            meta.field_type = c.extras.resource_data.field.field_type; // ✅ field_type!
          }

          // ✅ Criar option mock com resource_data
          if (c.extras.resource_data.id && c.extras.resource_data.option_label) {
            meta.options = [
              {
                id: c.extras.resource_data.id,
                option_label: c.extras.resource_data.option_label,
              },
            ];
            console.log('✅ Option criada do resource_data:', meta.options);
          }
        } else {
          // ✅ FALLBACK: Se NÃO tem resource_data, usar extras básicos
          console.log('⚠️ SEM resource_data - usando extras básicos:', c.extras);

          if (c.extras.field_uuid) {
            meta.field_uuid = c.extras.field_uuid;
          }
          if (c.extras.field_label) {
            meta.field_label = c.extras.field_label;
          }
          if (c.extras.form_uuid) {
            meta.form_uuid = c.extras.form_uuid;
          }
          // ✅ IMPORTANTE: field_type nos extras (salvo no POST)
          if (c.extras.field_type) {
            meta.field_type = c.extras.field_type;
            console.log('✅ field_type do extras:', c.extras.field_type);
          }
        }

        // Resource label (label da opção selecionada)
        if (c.extras.resource_label) {
          meta.field_label = meta.field_label || c.extras.resource_label;
        }

        // ✅ NOVO: option_value para submission.value (tags, textarea)
        if (c.extras.option_value) {
          meta.option_value = c.extras.option_value;
          console.log('📝 option_value hidratado:', c.extras.option_value);
        }

        // ✅ NOVO: geo_state e geo_city para submission.value (geo_state)
        if (c.extras.state && c.extras.city) {
          meta.geo_state = c.extras.state;
          meta.geo_city = c.extras.city;
          console.log('🌍 Geo hidratado:', { state: c.extras.state, city: c.extras.city });
        }

        // ✅ outro_value (já existente)
        if (c.extras.outro_value) {
          meta.outro_value = c.extras.outro_value;
        }
      }

      // ✅ Reconstruir right_operand para geo se necessário
      let rightValue = c.right_operand ?? '';

      // Se for geo_state e tiver state/city, reconstruir o formato "Estado | Cidade"
      if (c.extras?.state && c.extras?.city && !rightValue) {
        rightValue = `${c.extras.state} | ${c.extras.city}`;
        console.log('🌍 Reconstruindo right_operand para geo:', rightValue);
      }

      // ✅ Adicionar logicalOperator (herdado do grupo pai)
      // A primeira condição de um grupo não tem logicalOperator
      const leaf: ConditionLeaf = {
        id: uuidv4(),
        type: 'rule',
        left: c.left_operand ?? 'submission.option_id',
        operator: (c.operator ?? 'EQUALS') as Operator,
        right: rightValue,
        meta,
      };

      // Se não é a primeira condição E temos um parentLogicalOp, adicionar
      if (index > 0 && parentLogicalOp) {
        leaf.logicalOperator = parentLogicalOp;
      }

      return leaf;
    });
  };

  const rootServer = (byParent['root'] ?? []).find(x => x.is_group) ?? (byParent['root'] ?? [])[0];

  if (!rootServer) return undefined;

  const hierarchicalTree: ConditionGroup = {
    id: uuidv4(),
    type: 'group',
    logical: (rootServer.logical_operator ?? 'AND') as LogicalOp,
    children: attach(rootServer.id),
  };

  // ✅ OTIMIZAÇÃO UX: Achatar hierarquia em lista flat para edição
  // Mantém interface consistente entre criação e edição
  console.log('🔄 === ACHATANDO HIERARQUIA ===');
  console.log('📥 Hierarquia original:', hierarchicalTree);
  console.log('📊 Número de children no root:', hierarchicalTree.children.length);
  console.log(
    '📋 Children do root:',
    hierarchicalTree.children.map((c: ConditionNode) => ({
      type: c.type,
      id: c.id,
      ...(c.type === 'rule' && { field: (c as ConditionLeaf).meta?.field_label }),
    }))
  );

  const flattenedConditions = flattenHierarchy(hierarchicalTree);

  console.log('📤 Condições achatadas:', flattenedConditions.length);
  console.log(
    '📋 Lista flat:',
    flattenedConditions.map((c: ConditionLeaf, i: number) => ({
      index: i + 1,
      field: c.meta?.field_label || '?',
      logicalOp: c.logicalOperator || 'NONE',
    }))
  );
  console.log('🏁 === FIM ACHATAMENTO ===');

  // Retornar grupo com children flat
  return {
    id: hierarchicalTree.id,
    type: 'group',
    logical: hierarchicalTree.logical,
    children: flattenedConditions, // ✅ Lista flat ao invés de hierarquia!
  };
}

/** Garante número para order (backend pode mandar null/undefined). */
function coerceOrder(v: number | null | undefined): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/**
 * Nó "Flat" para criação — usa um id LOCAL (localId) e parentLocalId para
 * permitir um pipeline de criação (root → filhos) e mapear localId → parentId do servidor.
 */
export type FlatCreateNode =
  | {
      localId: string;
      type: 'group';
      logical: LogicalOp;
      parentLocalId: string | null;
      order: number;
    }
  | {
      localId: string;
      type: 'rule';
      left: string;
      operator: Operator;
      right: string | string[];
      parentLocalId: string | null;
      order: number;
    };

/**
 * Achata a árvore (draft) em uma lista ordenada de nós para criação.
 * - A raiz sempre vem primeiro (parentLocalId = null, type='group').
 * - Cada nível é ordenado pela posição do filho no array "children".
 * - Útil se você quiser orquestrar a criação sem recorrer a recursão direta no hook.
 */
export function treeToFlatForCreate(root: ConditionGroup | undefined): FlatCreateNode[] {
  if (!root) return [];
  const out: FlatCreateNode[] = [];
  const walk = (node: ConditionNode, parentLocalId: string | null, order: number) => {
    if (node.type === 'group') {
      const localId = node.id || uuidv4();
      out.push({
        localId,
        type: 'group',
        logical: node.logical,
        parentLocalId,
        order,
      });
      node.children?.forEach((child, idx) => walk(child, localId, idx));
    } else {
      const localId = node.id || uuidv4();
      out.push({
        localId,
        type: 'rule',
        left: node.left,
        operator: node.operator,
        right: normalizeRight(node.operator, node.right),
        parentLocalId,
        order,
      });
    }
  };

  // raiz (group) com parentLocalId=null e order=0
  walk(root, null, 0);
  return out;
}

/** Normaliza o right operand conforme operador (IN/NOT_IN → string[]; demais → string). */
export function normalizeRight(
  operator: Operator,
  value: string | string[] | undefined
): string | string[] {
  if (operator === 'IN' || operator === 'NOT_IN') {
    return Array.isArray(value) ? value : value ? [String(value)] : [];
  }
  if (Array.isArray(value)) return value[0] ?? '';
  return value ?? '';
}

/**
 * Plano simples de recriação:
 *  - Apaga todas as condições existentes (uuids);
 *  - Cria a sequência flat a partir da árvore atual.
 * A execução (delete → create) é responsabilidade de quem chamar.
 */
export function planRecreateConditions(
  existing: ConditionDTO[] | undefined,
  nextRoot: ConditionGroup | undefined
): { deleteUuids: string[]; createFlat: FlatCreateNode[] } {
  const deleteUuids = (existing ?? []).map(c => c.uuid);
  const createFlat = treeToFlatForCreate(nextRoot);
  return { deleteUuids, createFlat };
}

/**
 * Utilitário opcional: recalcula "order" dentro da árvore no draft,
 * sobrescrevendo a ordem de cada irmão de acordo com a posição no array.
 * Útil caso você permita reorder por drag & drop antes de persistir.
 */
export function ensureSequentialOrder(
  root: ConditionGroup | undefined
): ConditionGroup | undefined {
  if (!root) return root;
  const clone: ConditionGroup = {
    ...root,
    children: root.children?.map(c => ({ ...c })) ?? [],
  };

  const walk = (node: ConditionNode) => {
    if (node.type === 'group' && node.children?.length) {
      node.children.forEach((child, idx) => {
        // nada a persistir aqui no draft, mas garante consistência visual para treeToFlatForCreate
        // caso você queira guardar o "order" no draft: estenda os tipos com "order?: number"
        (child as any).__order = idx; // só anotação interna; não persiste
        if (child.type === 'group') walk(child);
      });
    }
  };

  walk(clone);
  return clone;
}

/**
 * (Opcional) Constrói a árvore a partir de um RuleDTO completo (útil quando você só tem o RuleDTO em mãos).
 */
export function dtoToConditionsTreeFromRule(rule: AutomationRuleDTO): ConditionGroup | undefined {
  return dtoConditionsToTree(rule.conditions ?? []);
}
